// PotDetails.js
import React, { useEffect, useState, useMemo } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ComposedChart,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  fetchMeasuresForPotByDate,
  fetchIrrigationsForPot,
} from "../../../api/api_pots";
import { fetchUserTimezoneInfo } from "../../../api/api_users";
import loadingGif from "../../assets/loading.gif";

/* ---------------- Time helpers ---------------- */

/** Parse any timestamp and return epoch ms. If naive string, treat it as UTC. */
function ensureUtcMs(input) {
  if (input instanceof Date) return input.getTime();
  if (typeof input === "number") return input;
  if (typeof input !== "string") return Number(input);
  if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(input)) return new Date(input).getTime();
  return new Date(input + "Z").getTime();
}

/** Build label formatter that respects DST for a given IANA tz */
function buildLabelFormatter(ianaTz) {
  const fmt = new Intl.DateTimeFormat(undefined, {
    timeZone: ianaTz,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return (ms) => fmt.format(new Date(ms)).replace(",", "");
}

/* ---------------- Domain helpers (unchanged logic) ---------------- */

const MAGNUS_A = 17.62;
const MAGNUS_B = 243.12;

function calculateDewPoint(temperature, humidity) {
  if (temperature == null || humidity == null) return null;
  const alpha =
    Math.log(humidity / 100) +
    (MAGNUS_A * temperature) / (MAGNUS_B + temperature);
  return (MAGNUS_B * alpha) / (MAGNUS_A - alpha);
}

function getRiskType(temperature, humidity, dewPoint) {
  if (temperature == null || humidity == null || dewPoint == null)
    return "noData";
  if (temperature < 0 && humidity < 50) return "blackFrost";
  if (temperature < 0 && humidity > 90) return "whiteFrost";
  if (temperature > 35 && humidity < 40) return "heatStress";
  if (humidity > 85 && temperature >= 5 && temperature <= 15) return "coldMold";
  if (humidity > 80 && temperature >= 18 && temperature <= 35)
    return "highMold";
  return "none";
}

const riskColors = {
  blackFrost: "#222831",
  whiteFrost: "#add8e6",
  heatStress: "#ff6600",
  coldMold: "#4fc3f7",
  highMold: "#ffcccb",
  none: "transparent",
};

const riskLegends = [
  { key: "highMold", color: "#ffcccb", label: "Very High Fungal Risk" },
  { key: "coldMold", color: "#4fc3f7", label: "High Cold-Fungi Risk" },
  { key: "whiteFrost", color: "#add8e6", label: "White Frost Risk" },
  { key: "blackFrost", color: "#222831", label: "Black Frost Risk" },
  { key: "heatStress", color: "#ff6600", label: "High Heat Stress" },
];

const legendColors = {
  temperatureSMA: "#22c55e",
  airHumiditySMA: "#3182ce",
};

const normalizeSoilMoistureMain = (value) => {
  if (value == null) return null;
  let normalized = ((3150 - value) / (3150 - 2150)) * 100;
  return Math.max(0, Math.min(100, normalized));
};

const normalizeSoilMoistureAux = (value) => {
  if (value == null) return null;
  let normalized = ((2550 - value) / (2550 - 2250)) * 100;
  return Math.max(0, Math.min(100, normalized));
};

const lightColors = {
  "No Light": "#1e3a5ccc",
  "Low Ambient Light": "#0a2342cc",
  "Ambient Light": "#d95f29c0",
  "Indirect Solar Light": "#ffc300cc",
  "Direct Solar Light": "#ffea00aa",
};

const roundToHalf = (num) => (num == null ? num : Math.round(num * 2) / 2);

const getLightCategory = (value, translations) => {
  if (value <= 10) return { name: translations.no_light || "No Light", num: 1 };
  if (value > 10 && value <= 100)
    return {
      name: translations.low_ambient_light || "Low Ambient Light",
      num: 2,
    };
  if (value > 100 && value <= 500)
    return { name: translations.ambient_light || "Ambient Light", num: 3 };
  if (value > 500 && value <= 3000)
    return {
      name: translations.indirect_solar_light || "Indirect Solar Light",
      num: 4,
    };
  if (value > 3000)
    return {
      name: translations.direct_solar_light || "Direct Solar Light",
      num: 5,
    };
  return { name: "", num: 0 };
};

/** Downsample on UTC buckets; presentation is formatted with user's TZ */
function fastDownsample(data, intervalMinutes = 60) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const bucketMs = intervalMinutes * 60 * 1000;
  let output = [];
  let sum = 0,
    count = 0;
  let firstMs = ensureUtcMs(data[0].time);
  let lastBucket = Math.floor(firstMs / bucketMs) * bucketMs;

  for (let d of data) {
    const ms = ensureUtcMs(d.time);
    const bucket = Math.floor(ms / bucketMs) * bucketMs;
    if (bucket !== lastBucket) {
      if (count > 0)
        output.push({
          time: new Date(lastBucket).toISOString(),
          value: sum / count,
        });
      sum = d.value;
      count = 1;
      lastBucket = bucket;
    } else {
      sum += d.value;
      count++;
    }
  }
  if (count > 0)
    output.push({
      time: new Date(lastBucket).toISOString(),
      value: sum / count,
    });
  return output;
}

function fastSMA(data, period = 5) {
  if (data.length < period) return data.map((x) => ({ ...x }));
  let result = [];
  let windowSum = 0;
  for (let i = 0; i < data.length; ++i) {
    windowSum += data[i].value;
    if (i >= period) windowSum -= data[i - period].value;
    if (i < period - 1) result.push({ ...data[i] });
    else result.push({ time: data[i].time, value: windowSum / period });
  }
  return result;
}

function conditionalSMA(data, period = 5, jumpThreshold = 25) {
  if (data.length < period) return data.map((x) => ({ ...x }));
  let result = [];
  let window = [];
  for (let i = 0; i < data.length; ++i) {
    const curr = data[i];
    if (i > 0 && curr.value - data[i - 1].value >= jumpThreshold) {
      result.push({ ...curr });
      window = [curr];
    } else {
      window.push(curr);
      if (window.length > period) window.shift();
      const avg = window.reduce((sum, x) => sum + x.value, 0) / window.length;
      result.push({ ...curr, value: avg });
    }
  }
  return result;
}

const initialRiskVisibility = {
  highMold: true,
  coldMold: true,
  whiteFrost: true,
  blackFrost: true,
  heatStress: true,
};

const CustomLegend = ({ payload, onClick, visible }) => (
  <div className="custom-legend-container">
    {payload.map((entry) => {
      const color = legendColors[entry.dataKey] || "#aaa";
      const isActive = visible[entry.dataKey];
      return (
        <span
          key={entry.dataKey}
          onClick={() => onClick && onClick({ dataKey: entry.dataKey })}
          className={`custom-legend-item${isActive ? "" : " inactive"}`}
        >
          <span
            className={`custom-legend-bar${
              !isActive ? " custom-legend-bar-inactive" : ""
            }`}
            style={{ background: color }}
          />
          {entry.value}
        </span>
      );
    })}
  </div>
);

/* ---------------- Component ---------------- */

const PotDetails = () => {
  const { translations } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { pot } = location.state;

  // User IANA time zone
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const [tzLoading, setTzLoading] = useState(true);

  // Build formatter with DST using user's IANA
  const formatTick = useMemo(
    () => buildLabelFormatter(userTimeZone),
    [userTimeZone]
  );

  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [selectedTimeRange, setSelectedTimeRange] = useState();

  const [irrigations, setIrrigations] = useState([]);
  const [rawPotData, setRawPotData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [visibleTempAir, setVisibleTempAir] = useState({
    temperatureSMA: true,
    airHumiditySMA: true,
  });
  const [visibleSoilMoisture, setVisibleSoilMoisture] = useState({
    smaValue: true,
    smaValue2: true,
  });
  const [visibleSunlight, setVisibleSunlight] = useState({
    lightCategoryNum: true,
  });
  const [visibleRisks, setVisibleRisks] = useState(initialRiskVisibility);

  const handleLegendClickTempAir = (e) => {
    setVisibleTempAir((prev) => ({ ...prev, [e.dataKey]: !prev[e.dataKey] }));
  };
  const handleLegendClickSoilMoisture = (e) => {
    setVisibleSoilMoisture((prev) => ({
      ...prev,
      [e.dataKey]: !prev[e.dataKey],
    }));
  };
  const handleLegendClickRisk = (key) => {
    setVisibleRisks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const timeRangeOptions = [
    translations.one_week,
    translations.two_weeks,
    translations.one_month,
    translations.three_months,
    translations.six_months,
    translations.one_year,
  ];

  // Fetch user's IANA time zone once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tzRes = await fetchUserTimezoneInfo();
        if (!mounted) return;
        if (!tzRes.error && tzRes.data?.iana) {
          setUserTimeZone(tzRes.data.iana);
        } else {
          setUserTimeZone("UTC");
        }
      } catch {
        setUserTimeZone("UTC");
      } finally {
        if (mounted) setTzLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Update dates when selecting quick ranges
  useEffect(() => {
    if (!selectedTimeRange) return;
    const today = new Date();
    setEndDate(new Date(today));
    const sd = new Date(today);
    switch (selectedTimeRange) {
      case translations.one_week:
        sd.setDate(today.getDate() - 7);
        break;
      case translations.two_weeks:
        sd.setDate(today.getDate() - 14);
        break;
      case translations.one_month:
        sd.setDate(today.getDate() - 30);
        break;
      case translations.three_months:
        sd.setMonth(today.getMonth() - 3);
        break;
      case translations.six_months:
        sd.setMonth(today.getMonth() - 6);
        break;
      case translations.one_year:
        sd.setFullYear(today.getFullYear() - 1);
        break;
      default:
        break;
    }
    setStartDate(sd);
  }, [selectedTimeRange, translations]);

  // Load pot data + irrigations
  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      (async () => {
        const token = localStorage.getItem("userToken");
        const r = await fetchMeasuresForPotByDate(
          pot.id,
          startDate,
          endDate,
          token
        );
        return r?.data || [];
      })(),
      (async () => {
        const token = localStorage.getItem("userToken");
        const r = await fetchIrrigationsForPot(pot.id, token);
        return r?.error ? [] : r?.data || [];
      })(),
    ]).then(([potData, irrigs]) => {
      setRawPotData(Array.isArray(potData) ? potData : []);
      setIrrigations(Array.isArray(irrigs) ? irrigs : []);
      setIsLoading(false);
    });
  }, [startDate, endDate, selectedTimeRange, pot.id, pot.hub_id]);

  useEffect(() => {
    if (!selectedTimeRange && translations?.one_week) {
      setSelectedTimeRange(translations.one_week);
    }
  }, [translations]);


  // Sort raw data by time asc (using epoch ms to be robust)
  const orderedRawPotData = useMemo(
    () =>
      Array.isArray(rawPotData)
        ? [...rawPotData].sort(
            (a, b) => ensureUtcMs(a.timestamp) - ensureUtcMs(b.timestamp)
          )
        : [],
    [rawPotData]
  );

  // Build chart series; add timeMs for numeric X axis
  const chartData = useMemo(() => {
    if (!Array.isArray(orderedRawPotData) || orderedRawPotData.length === 0) {
      return {
        temperature: [],
        airHumidity: [],
        soilMoisture: [],
        soilMoisture_2: [],
        illumination: [],
        temperatureSMA: [],
        airHumiditySMA: [],
        soilMoistureSMA: [],
        soilMoistureSMA_2: [],
      };
    }

    const temperature = fastDownsample(
      orderedRawPotData.map((d) => ({
        time: d.timestamp,
        value: Number(d.temperature),
      }))
    ).map((d) => ({
      ...d,
      timeMs: ensureUtcMs(d.time),
      value: roundToHalf(d.value),
    }));

    const airHumidity = fastDownsample(
      orderedRawPotData.map((d) => ({
        time: d.timestamp,
        value: d.air_humidity,
      }))
    ).map((d) => ({
      ...d,
      timeMs: ensureUtcMs(d.time),
      value: roundToHalf(d.value),
    }));

    const soilMoisture = fastDownsample(
      orderedRawPotData.map((d) => ({
        time: d.timestamp,
        value: normalizeSoilMoistureMain(d.ground_humidity),
      }))
    ).map((d) => ({ ...d, timeMs: ensureUtcMs(d.time) }));

    const soilMoisture_2 = fastDownsample(
      orderedRawPotData.map((d) => ({
        time: d.timestamp,
        value: normalizeSoilMoistureAux(d.ground_humidity_2),
      }))
    ).map((d) => ({ ...d, timeMs: ensureUtcMs(d.time) }));

    const illumination = fastDownsample(
      orderedRawPotData.map((d) => ({
        time: d.timestamp,
        value: d.illumination,
      }))
    ).map((d) => ({ ...d, timeMs: ensureUtcMs(d.time) }));

    const temperatureSMA = fastSMA(temperature, 5).map((i) => ({
      ...i,
      value: roundToHalf(i.value),
      timeMs: ensureUtcMs(i.time),
    }));
    const airHumiditySMA = fastSMA(airHumidity, 5).map((i) => ({
      ...i,
      value: roundToHalf(i.value),
      timeMs: ensureUtcMs(i.time),
    }));
    const soilMoistureSMA = conditionalSMA(soilMoisture, 5, 25).map((i) => ({
      ...i,
      timeMs: ensureUtcMs(i.time),
    }));
    const soilMoistureSMA_2 = conditionalSMA(soilMoisture_2, 5, 25).map(
      (i) => ({ ...i, timeMs: ensureUtcMs(i.time) })
    );

    return {
      temperature,
      airHumidity,
      soilMoisture,
      soilMoisture_2,
      illumination,
      temperatureSMA,
      airHumiditySMA,
      soilMoistureSMA,
      soilMoistureSMA_2,
    };
  }, [orderedRawPotData]);

  // Compose temp/humidity/risk line+bar dataset
  const tempHumidityData = useMemo(() => {
    const humidityMap = Object.fromEntries(
      (chartData.airHumiditySMA || []).map((item) => [item.timeMs, item.value])
    );
    return (chartData.temperatureSMA || [])
      .map((item) => {
        const temperature = item.value;
        const humidity = humidityMap[item.timeMs];
        const dewPoint = calculateDewPoint(temperature, humidity);
        const riskType = getRiskType(temperature, humidity, dewPoint);
        return {
          timeMs: item.timeMs,
          temperatureSMA: temperature,
          airHumiditySMA: humidity,
          dewPoint: dewPoint != null ? Math.round(dewPoint * 10) / 10 : null,
          riskType,
        };
      })
      .sort((a, b) => a.timeMs - b.timeMs);
  }, [chartData.temperatureSMA, chartData.airHumiditySMA]);

  // Soil humidity series with SMA alignment on timeMs
  const groundHumidityData = useMemo(
    () =>
      (chartData.soilMoisture || []).map((item) => {
        const rawSmaValue = (chartData.soilMoistureSMA || []).find(
          (sma) => sma.timeMs === item.timeMs
        )?.value;
        const rawSmaValue2 = (chartData.soilMoistureSMA_2 || []).find(
          (sma) => sma.timeMs === item.timeMs
        )?.value;
        const clamp = (v) =>
          v == null
            ? null
            : Math.min(100, Math.max(0, Number(Number(v).toFixed(1))));
        return {
          timeMs: item.timeMs,
          smaValue: clamp(rawSmaValue),
          smaValue2: clamp(rawSmaValue2),
        };
      }),
    [
      chartData.soilMoisture,
      chartData.soilMoistureSMA,
      chartData.soilMoistureSMA_2,
    ]
  );

  // Illumination with category; keep timeMs
  const illuminationWithCategory = useMemo(
    () =>
      (chartData.illumination || []).map((item) => {
        const cat = getLightCategory(item.value, translations);
        return {
          timeMs: item.timeMs,
          lightCategory: cat.name,
          lightCategoryNum: cat.num,
        };
      }),
    [chartData.illumination, translations]
  );

  // round a timestamp down/up to the nearest hour
  const floorToHour = (ms) => Math.floor(ms / 3600000) * 3600000;
  const ceilToHour = (ms) => Math.ceil(ms / 3600000) * 3600000;

  const xDomain = useMemo(() => {
    const times = [];

    const safeArr = (arr) => (Array.isArray(arr) ? arr : []);

    // series currently plotted
    safeArr(tempHumidityData).forEach(
      (d) => d?.timeMs != null && times.push(d.timeMs)
    );
    safeArr(groundHumidityData).forEach(
      (d) => d?.timeMs != null && times.push(d.timeMs)
    );
    safeArr(illuminationWithCategory).forEach(
      (d) => d?.timeMs != null && times.push(d.timeMs)
    );

    // irrigation lines within date filter (keep them but don't expand beyond filters)
    const minFilter = startDate.getTime();
    const maxFilter = endDate.getTime();
    safeArr(irrigations).forEach((irrig) => {
      const t = ensureUtcMs(irrig.startDate);
      if (t >= minFilter && t <= maxFilter) times.push(t);
    });

    if (times.length === 0) {
      const now = Date.now();
      return [floorToHour(now - 7 * 24 * 60 * 60 * 1000), ceilToHour(now)];
    }

    const rawMin = Math.min(...times);
    const rawMax = Math.max(...times);

    // No padding. Snap to full hours so ticks are pretty.
    const minX = floorToHour(rawMin);
    const maxX = ceilToHour(rawMax);

    return [minX, maxX];
  }, [
    tempHumidityData,
    groundHumidityData,
    illuminationWithCategory,
    irrigations,
    startDate,
    endDate,
  ]);

  // Draw irrigation reference lines only if inside current xDomain
  const renderIrrigationLines = () => {
    const [minX, maxX] = xDomain;
    const safeArr = (arr) => (Array.isArray(arr) ? arr : []);
    return safeArr(irrigations).map((irrigation) => {
      const ms = ensureUtcMs(irrigation.startDate);
      if (ms < minX || ms > maxX) return null;
      const h0 = new Date(ms);
      h0.setMinutes(0, 0, 0);
      return (
        <ReferenceLine key={irrigation.id} x={h0.getTime()} stroke="blue" />
      );
    });
  };

  const RiskLegend = () => (
    <div className="risk-legend-container">
      {riskLegends.map(({ key, color, label }) => (
        <div
          key={key}
          className={`risk-legend-item${visibleRisks[key] ? "" : " inactive"}`}
          onClick={() => handleLegendClickRisk(key)}
        >
          <div
            className={`risk-legend-bar${
              !visibleRisks[key] ? " risk-legend-bar-inactive" : ""
            }`}
            style={{ backgroundColor: color }}
          />
          {translations[key] || label}
        </div>
      ))}
    </div>
  );

  if (isLoading || tzLoading) {
    return (
      <div className="pot-loading">
        <img src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  const noDataForRangeText =
    translations.no_data_for_range;

  return (
    <div className="pot-page">
      <div className="pot-container">
        <div className="header">
          <button onClick={() => navigate(-1)} className="back-button">
            &#8592;
          </button>
          <h1>{pot.name}</h1>
        </div>

        <div className="date-picker-container">
          <ReactDatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            dateFormat="dd/MM/yyyy"
          />
          <ReactDatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            dateFormat="dd/MM/yyyy"
          />
        </div>

        <select
          className="irrigate-select2"
          value={selectedTimeRange || ""}
          onChange={(e) => setSelectedTimeRange(e.target.value)}
        >
          <option value="" disabled>
            {translations.select_time_range}
          </option>
          {timeRangeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {/* Temp + Humidity */}
        <div className="graph-container">
          <h2 className="chart-title">
            {translations.temperature_airHumidity}
          </h2>
          {tempHumidityData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={420}>
                <ComposedChart data={tempHumidityData}>
                  <defs>
                    <linearGradient
                      id="temperatureGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      {tempHumidityData.map((point, idx) => {
                        let color;
                        if (
                          point.temperatureSMA >= pot.min_temperature &&
                          point.temperatureSMA <= pot.max_temperature
                        )
                          color = "#22c55e";
                        else if (point.temperatureSMA > pot.max_temperature)
                          color = "#dc2626";
                        else color = "#2563eb";
                        const offset =
                          (idx / Math.max(1, tempHumidityData.length - 1)) *
                          100;
                        return (
                          <stop
                            key={idx}
                            offset={`${offset}%`}
                            stopColor={color}
                          />
                        );
                      })}
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="timeMs"
                    type="number"
                    domain={xDomain}
                    allowDataOverflow={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatTick}
                    padding={{ left: 0, right: 0 }}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={[5, 40]}
                    label={{
                      value: translations.temperature + " (°C)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[35, 95]}
                    label={{
                      value: translations.air_humidity + " (%)",
                      angle: 90,
                      position: "insideRight",
                      offset: 10,
                    }}
                    stroke="#3182ce"
                    tick={{ fill: "#3182ce" }}
                  />

                  {tempHumidityData.map((d, index) => {
                    if (index === tempHumidityData.length - 1) return null;
                    if (!d || !tempHumidityData[index + 1]) return null;
                    if (!visibleRisks[d.riskType]) return null;
                    return (
                      <ReferenceArea
                        key={`riskArea-${index}`}
                        yAxisId="left"
                        x1={d.timeMs}
                        x2={tempHumidityData[index + 1].timeMs}
                        stroke="none"
                        fill={riskColors[d.riskType] || "transparent"}
                        fillOpacity={0.4}
                      />
                    );
                  })}

                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "temperatureSMA")
                        return [
                          `${value?.toFixed(1)} °C`,
                          translations.temperature,
                        ];
                      if (name === "airHumiditySMA")
                        return [
                          `${value?.toFixed(1)} %`,
                          translations.air_humidity,
                        ];
                      if (name === "dewPoint")
                        return [
                          `${value?.toFixed(1)} °C`,
                          translations.dew_point || "Dew Point",
                        ];
                      return value;
                    }}
                    labelFormatter={(valueMs) => formatTick(valueMs)}
                  />

                  <Legend
                    content={(props) => (
                      <CustomLegend
                        {...props}
                        onClick={handleLegendClickTempAir}
                        visible={visibleTempAir}
                      />
                    )}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="temperatureSMA"
                    name={translations.temperature}
                    stroke="url(#temperatureGradient)"
                    strokeWidth={4}
                    dot={false}
                    hide={!visibleTempAir.temperatureSMA}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="airHumiditySMA"
                    name={translations.air_humidity}
                    barSize={18}
                    radius={[3, 3, 0, 0]}
                    opacity={0.8}
                    hide={!visibleTempAir.airHumiditySMA}
                    isAnimationActive={false}
                    shape={(props) => {
                      const { x, y, width, height, payload } = props;
                      const value = payload.airHumiditySMA;
                      let color = "#60a5fa40";
                      if (
                        value < pot.min_air_humidity - 5 ||
                        value > pot.max_air_humidity + 5
                      )
                        color = "#ef444440";
                      else if (
                        value < pot.min_air_humidity ||
                        value > pot.max_air_humidity
                      )
                        color = "#f59e0b40";
                      return (
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={color}
                          rx={3}
                          ry={3}
                        />
                      );
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="dewPoint"
                    name={translations.dew_point || "Dew Point"}
                    stroke="#8884d8"
                    strokeDasharray="3 3"
                    dot={false}
                    hide={true}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <RiskLegend />
            </>
          ) : (
            <p>{noDataForRangeText}</p>
          )}
        </div>

        {/* Ground humidity */}
        <div className="graph-container">
          <h2 className="chart-title">
            {translations.ground_humidity + " (%)"}
          </h2>
          {chartData.soilMoisture.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={groundHumidityData}>
                <defs>
                  <linearGradient id="water1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#67e8f9" stopOpacity={0.9} />
                    <stop offset="40%" stopColor="#38bdf8" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.33} />
                  </linearGradient>
                  <linearGradient id="water2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#c7d2fe" stopOpacity={0.8} />
                    <stop offset="40%" stopColor="#a5b4fc" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.33} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="timeMs"
                  type="number"
                  domain={xDomain}
                  allowDataOverflow={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatTick}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip labelFormatter={(valueMs) => formatTick(valueMs)} />
                <Legend onClick={handleLegendClickSoilMoisture} />
                {renderIrrigationLines()}
                <ReferenceArea
                  y1={0}
                  y2={100}
                  strokeOpacity={0}
                  fill="#ffffff"
                  fillOpacity={0.1}
                  label={{
                    value: translations.ideal_zone,
                    position: "top",
                    fontSize: 13,
                    fill: "#4ade80",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="smaValue"
                  stroke="#0ea5e9"
                  strokeWidth={3}
                  fill="url(#water1)"
                  fillOpacity={1}
                  name="Main"
                  activeDot={{ r: 7 }}
                  dot={false}
                  isAnimationActive={true}
                  hide={!visibleSoilMoisture.smaValue}
                />
                <Area
                  type="monotone"
                  dataKey="smaValue2"
                  stroke="#6366f1"
                  strokeWidth={3}
                  fill="url(#water2)"
                  fillOpacity={0.85}
                  name="Aux"
                  activeDot={{ r: 7 }}
                  dot={false}
                  isAnimationActive={true}
                  hide={!visibleSoilMoisture.smaValue2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p>{noDataForRangeText}</p>
          )}
        </div>

        {/* Sunlight */}
        <div className="graph-container">
          <h2 className="chart-title">{translations.sunlight_level}</h2>
          {illuminationWithCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={illuminationWithCategory}>
                <XAxis
                  dataKey="timeMs"
                  type="number"
                  domain={xDomain}
                  allowDataOverflow={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatTick}
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis
                  type="number"
                  domain={[0, 6]}
                  tick={{ fontSize: 18 }}
                  ticks={[0.1, 2, 3, 4, 5]}
                  tickFormatter={(value) => {
                    switch (value) {
                      case 0.1:
                        return "🌙";
                      case 2:
                        return "💡";
                      case 3:
                        return "☁️";
                      case 4:
                        return "🌤️";
                      case 5:
                        return "☀️";
                      default:
                        return "🌙";
                    }
                  }}
                />
                <Tooltip
                  formatter={(_, __, { payload }) => payload.lightCategory}
                  labelFormatter={(valueMs) => formatTick(valueMs)}
                />
                <Bar
                  dataKey="lightCategoryNum"
                  name={translations.sunlight_level}
                  radius={[6, 6, 0, 0]}
                  hide={!visibleSunlight.lightCategoryNum}
                >
                  {illuminationWithCategory.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={lightColors[entry.lightCategory]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p>{noDataForRangeText}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PotDetails;
